import type {
  SuperbarMessage,
  SuperbarApi,
  SuperbarState,
  SuperbarStateOptions,
  SuperbarEventListener,
  SuperbarError,
  PurchaseData,
} from '../api/superbar.d';
import {
  DisplayMode,
  SuperBarViewState,
  type DisplayMessage,
} from '../chat-view/context/use-chat-context';
import {
  sendOnboardingMessage,
  sendConversionEvent,
} from '../chat-view/services/chat-api.service';
import type { Session, Store } from '../chat-view/services/types';
import { AnalyticsEvent } from '../chat-view/constants/analyticsEvents';
import * as amplitude from '@amplitude/analytics-browser';
import { useStoreHostnameStore } from '../stores/useStoreHostnameStore';
import { useStoreInfoStore } from '../stores/useStoreInfoStore';
import { purchaseDetector } from '../chat-view/services/purchase-detector';

/**
 * Internal interface for chat context dependencies.
 * This abstracts the React context to allow proper dependency injection
 * and testing of the SuperbarWindowApi implementation.
 */
interface ChatContext {
  setDisplayMessage: (message: DisplayMessage | undefined) => void;
  /** Sets the display mode of the chat view */
  setDisplayMode: (mode: DisplayMode) => void;
  /** Updates the visual state of the Superbar */
  setSuperBarViewState: (state: SuperBarViewState) => void;
  /** Retrieves the current visual state of the Superbar */
  getSuperBarViewState: () => SuperBarViewState;
  /** Clears all chat state and messages */
  clearAll: () => void;
  /** Current user session for onboarding messages */
  currentSession?: Session;
  currentStore?: Store;
}

class SuperbarWindowApi implements SuperbarApi {
  private readonly listeners: Set<SuperbarEventListener> = new Set();
  private currentAction: 'expand' | 'minimize' | 'close' | null = null;

  constructor(private readonly chatContext: ChatContext) {}

  /**
   * Creates a structured error object
   */
  private createError(
    code: SuperbarError['code'],
    message: string
  ): SuperbarError {
    return { code, message };
  }

  /**
   * Type guard for SuperbarError
   */
  private isSuperbarError(error: unknown): error is SuperbarError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      'message' in error
    );
  }

  /**
   * Displays a message in the Superbar.
   * @param message - Either a plain string or a structured SuperbarMessage object
   */
  sendMessage(message: string | SuperbarMessage): void {
    let processedMessage: SuperbarMessage;
    const isStringMessage = typeof message === 'string';

    if (isStringMessage) {
      processedMessage = {
        userQuery: '',
        systemAnswer: message,
      };
    } else {
      processedMessage = message;
    }

    this.trackEventIfAmplitudeEnabled(
      AnalyticsEvent.SUPERBAR_API_SEND_MESSAGE,
      {
        message_type: isStringMessage ? 'string' : 'structured',
        has_cta: !!processedMessage.suggestedCTA,
        has_code: !!processedMessage.hasCode,
        has_auto_hide: processedMessage.autoHideTimeout !== undefined,
        auto_hide_timeout: processedMessage.autoHideTimeout,
      }
    );

    this.chatContext.setDisplayMessage({
      userQuery: processedMessage.userQuery,
      systemAnswer: processedMessage.systemAnswer,
      suggestedCTA: processedMessage.suggestedCTA,
      hasCode: processedMessage.hasCode,
      autoHideTimeout: processedMessage.autoHideTimeout,
    });

    this.chatContext.setDisplayMode(DisplayMode.ASSISTANT);
    this.chatContext.setSuperBarViewState(SuperBarViewState.DISPLAYING_ANSWER);
  }

  /**
   * Clears the current message and resets the Superbar display.
   */
  clearMessage(): void {
    this.trackEventIfAmplitudeEnabled(
      AnalyticsEvent.SUPERBAR_API_CLEAR_MESSAGE
    );
    this.chatContext.clearAll();
  }

  /**
   * Creates and sends an onboarding message through the chat system.
   * @param userMessage - The user's message to process
   * @returns Promise that resolves when the message is processed
   * @throws Error if session is not available
   */
  async createOnboardingMessage(userMessage: string): Promise<void> {
    if (!this.chatContext.currentSession) {
      this.trackEvent(AnalyticsEvent.SUPERBAR_API_ONBOARDING_MESSAGE_ERROR, {
        error: 'session_not_available',
      });
      throw this.createError(
        'INVALID_STATE',
        'Session not available for onboarding message'
      );
    }

    this.trackEvent(AnalyticsEvent.SUPERBAR_API_ONBOARDING_MESSAGE_STARTED, {
      message_length: userMessage.length,
    });

    try {
      const response = await sendOnboardingMessage({
        userMessage,
        session: this.chatContext.currentSession,
      });

      this.trackEvent(AnalyticsEvent.SUPERBAR_API_ONBOARDING_MESSAGE_SUCCESS, {
        onboarding_step: response.onboardingStep,
        has_cta: !!response.suggestedCTA,
      });

      this.chatContext.setDisplayMessage({
        userQuery: response.query,
        systemAnswer: response.answer,
        suggestedCTA: response.suggestedCTA,
        autoHideTimeout: response.autoHideTimeout,
      });

      this.chatContext.setDisplayMode(DisplayMode.ASSISTANT);
      this.chatContext.setSuperBarViewState(SuperBarViewState.DEFAULT);

      // Dispatch event to notify superserve-ui about onboarding state change
      const eventData = {
        type: 'SUPERBAR_CHAT_MESSAGE_RECEIVED',
        payload: {
          onboardingStep: response.onboardingStep,
          timestamp: new Date().toISOString(),
        },
      };

      window.dispatchEvent(
        new CustomEvent('SUPERBAR_EVENT', {
          detail: eventData,
        })
      );
    } catch (error) {
      this.trackEvent(AnalyticsEvent.SUPERBAR_API_ONBOARDING_MESSAGE_ERROR, {
        error: error instanceof Error ? error.message : 'unknown_error',
      });
      console.error('Error sending onboarding message:', error);

      if (this.isSuperbarError(error)) {
        throw error;
      }
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      throw this.createError(
        'INVALID_STATE',
        `Failed to send onboarding message: ${errorMessage}`
      );
    }
  }

  /**
   * Converts internal SuperBarViewState to public API SuperbarState
   */
  private mapViewStateToPublicState(
    viewState: SuperBarViewState
  ): SuperbarState {
    switch (viewState) {
      case SuperBarViewState.DEFAULT:
        return 'expanded';
      case SuperBarViewState.MINIMIZED:
        return 'minimized';
      case SuperBarViewState.CLOSED:
        return 'closed';
      default:
        return 'expanded';
    }
  }

  /**
   * Emits state change event to all listeners
   */
  private emitStateChange(newState: SuperbarState): void {
    this.listeners.forEach((listener) => {
      try {
        listener(newState);
      } catch (error) {
        console.error('Error in Superbar state change listener:', error);
      }
    });
  }

  private get isAmplitudeEnabled(): boolean {
    const sessionSource = useStoreInfoStore.getState().sessionSource;
    if (sessionSource === 'playground') return true;
    return (
      this.chatContext.currentStore?.config?.config?.amplitudeEnabled === true
    );
  }

  private trackEventIfAmplitudeEnabled(
    eventName: string,
    eventProperties?: Record<string, unknown>
  ): void {
    if (!this.isAmplitudeEnabled) return;
    this.trackEvent(eventName, eventProperties);
  }

  /**
   * Tracks analytics events for API actions
   */
  private trackEvent(
    eventName: string,
    eventProperties?: Record<string, unknown>
  ): void {
    try {
      amplitude.track(eventName, {
        api: 'superbar',
        timestamp: new Date().toISOString(),
        ...eventProperties,
      });
    } catch (error) {
      console.error('Failed to track Superbar API event:', error);
    }
  }

  /**
   * Tracks state change events
   */
  private trackStateChange(
    action: 'expand' | 'minimize' | 'close',
    fromState: SuperbarState,
    toState: SuperbarState,
    source?: string
  ): void {
    const eventMap = {
      expand: AnalyticsEvent.SUPERBAR_API_EXPAND,
      minimize: AnalyticsEvent.SUPERBAR_API_MINIMIZE,
      close: AnalyticsEvent.SUPERBAR_API_CLOSE,
    };

    this.trackEventIfAmplitudeEnabled(eventMap[action], {
      source: source || 'api_call',
      from_state: fromState,
      to_state: toState,
    });
  }

  /**
   * Executes a state transition with duplicate prevention
   */
  private executeTransition(
    targetState: SuperBarViewState,
    action: 'expand' | 'minimize' | 'close',
    source?: string
  ): void {
    const currentViewState = this.chatContext.getSuperBarViewState();
    const fromState = this.mapViewStateToPublicState(currentViewState);
    const toState = this.mapViewStateToPublicState(targetState);

    // If already in target state, do nothing
    if (currentViewState === targetState) {
      return;
    }

    // If same action is already in progress, ignore duplicate
    if (this.currentAction === action) {
      return;
    }

    // Mark action as in progress
    this.currentAction = action;

    // Execute state change
    this.chatContext.setSuperBarViewState(targetState);

    // Track analytics
    this.trackStateChange(action, fromState, toState, source);

    // Emit state change event
    this.emitStateChange(toState);

    // Clear action after animation (300ms)
    setTimeout(() => {
      this.currentAction = null;
    }, 300);
  }

  /**
   * Expands the Superbar
   */
  expand(opts?: SuperbarStateOptions): void {
    this.executeTransition(SuperBarViewState.DEFAULT, 'expand', opts?.source);
  }

  /**
   * Minimizes the Superbar
   */
  minimize(opts?: SuperbarStateOptions): void {
    this.executeTransition(
      SuperBarViewState.MINIMIZED,
      'minimize',
      opts?.source
    );
  }

  /**
   * Closes the Superbar (fully hides until re-opened)
   */
  close(opts?: SuperbarStateOptions): void {
    this.executeTransition(SuperBarViewState.CLOSED, 'close', opts?.source);
  }

  /**
   * Gets the current state of the Superbar
   */
  getState(): SuperbarState {
    const currentViewState = this.chatContext.getSuperBarViewState();
    return this.mapViewStateToPublicState(currentViewState);
  }

  /**
   * Subscribes to state change events
   * @returns Unsubscribe function
   */
  on(event: 'statechange', callback: SuperbarEventListener): () => void {
    if (event !== 'statechange') {
      throw new Error(`Unknown event type: ${event}`);
    }

    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Sets a custom store hostname dynamically
   * @param hostname - The hostname to set
   * @throws Error if hostname is invalid
   */
  setStoreHostname(hostname: string): void {
    try {
      useStoreHostnameStore.getState().setCustomHostname(hostname);
    } catch (error) {
      if (error instanceof Error) {
        throw this.createError('INVALID_STATE', error.message);
      }
      throw this.createError('INVALID_STATE', 'Failed to set store hostname');
    }
  }

  /**
   * Clears the custom store hostname
   */
  clearStoreHostname(): void {
    useStoreHostnameStore.getState().clearCustomHostname();
  }

  /**
   * Gets the current store hostname
   * @returns The active store hostname
   */
  getStoreHostname(): string {
    return useStoreHostnameStore.getState().getHostname();
  }

  /**
   * Tracks a completed purchase. The returned Promise always resolves (never
   * rejects) so it cannot break the merchant's checkout flow.
   * Deduplicates with auto-detected purchases by orderId.
   */
  async trackPurchase(
    data: PurchaseData
  ): Promise<{ success: boolean; error?: string }> {
    if (data.orderId) {
      purchaseDetector.markOrderIdTracked(data.orderId);
    }

    const validationError = this.getValidationError(data);
    if (validationError) {
      console.warn(validationError);
      return { success: false, error: validationError };
    }

    const { currentSession, currentStore } = this.chatContext;
    if (!currentSession || !currentStore) {
      const message = 'Cannot track purchase: session or store not available';
      console.warn(message);
      this.trackEventIfAmplitudeEnabled(
        AnalyticsEvent.SUPERBAR_API_TRACK_PURCHASE,
        {
          error: 'session_or_store_not_available',
        }
      );
      return { success: false, error: message };
    }

    this.trackEventIfAmplitudeEnabled(
      AnalyticsEvent.SUPERBAR_API_TRACK_PURCHASE,
      {
        order_value: data.value,
        currency: data.currency,
        has_order_id: !!data.orderId,
      }
    );

    const eventData: Record<string, unknown> = {
      orderValue: data.value,
      currency: data.currency,
    };
    if (data.orderId) {
      eventData.orderId = data.orderId;
    }

    try {
      await sendConversionEvent({
        storeId: currentStore.id,
        session: currentSession,
        data: eventData,
      });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Failed to track purchase event:', error);
      return { success: false, error: message };
    }
  }

  private getValidationError(data: PurchaseData): string | null {
    if (!data.value || data.value <= 0 || Number.isNaN(data.value)) {
      return 'trackPurchase: value must be a positive number';
    }
    if (!data.currency || data.currency.trim() === '') {
      return 'trackPurchase: currency is required';
    }
    return null;
  }
}

export { SuperbarWindowApi, type ChatContext };
